import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  User, Car as CarIcon, Calendar as CalendarIcon, ClipboardCheck, Camera,
  PenLine, Check, ChevronRight, ChevronLeft, Loader2, Plus, Search, X,
  Fuel, Gauge, FileText, Eraser, CheckCircle2,
} from "lucide-react";
import { format, addDays, differenceInCalendarDays } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import MovixLogo from "@/components/MovixLogo";
import PhotoGallery from "@/components/PhotoGallery";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

const CHECKLIST_KEYS = [
  "exterior_clean", "interior_clean", "no_damage_body", "no_damage_glass",
  "tires_ok", "spare_tire", "all_lights_ok", "wipers_ok",
  "documents_present", "key_count_ok", "manual_present", "registration_present",
];

const STEPS = [
  { key: "customer",  icon: User },
  { key: "car",       icon: CarIcon },
  { key: "period",    icon: CalendarIcon },
  { key: "checklist", icon: ClipboardCheck },
  { key: "photos",    icon: Camera },
  { key: "signature", icon: PenLine },
];

export default function RentalWizard() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  // ---------- Data state ----------
  const [customer, setCustomer] = useState(null);          // selected customer object
  const [car, setCar] = useState(null);                    // selected car object
  const [period, setPeriod] = useState({
    pickup_date: new Date(),
    return_date: addDays(new Date(), 7),
    billing_period: "semanal",
    deposit: 500,
  });
  const [checklist, setChecklist] = useState({
    items: Object.fromEntries(CHECKLIST_KEYS.map((k) => [k, false])),
    fuel_level: 100,
    odometer: "",
    notes: "",
  });
  const [contractLang, setContractLang] = useState(lang);
  const [rentalId, setRentalId] = useState(null);          // created when leaving step 3
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState(false);

  // ---------- Pricing computation ----------
  // Weekly rate is the primary stored value. Daily = weekly / 7.
  const days = Math.max(1, differenceInCalendarDays(period.return_date, period.pickup_date));
  const weeks = Math.max(1, Math.ceil(days / 7));
  const qty = period.billing_period === "semanal" ? weeks : days;
  const weeklyRate = car ? Number(car.weekly_rate || (car.daily_price ? car.daily_price * 7 : 0)) : 0;
  const dailyRate = +(weeklyRate / 7).toFixed(2);
  const rate = period.billing_period === "semanal" ? weeklyRate : dailyRate;
  const rentalTotal = +(rate * qty).toFixed(2);
  const totalDue = +(rentalTotal + Number(period.deposit || 0)).toFixed(2);

  // ---------- Step gating ----------
  const canNext = useMemo(() => {
    if (step === 0) return !!customer;
    if (step === 1) return !!car;
    if (step === 2) return rentalTotal > 0 && period.return_date > period.pickup_date;
    if (step === 3) return true; // checklist optional but encouraged
    if (step === 4) return true; // photos optional
    return false;
  }, [step, customer, car, rentalTotal, period]);

  // ---------- Transitions ----------
  const goNext = async () => {
    if (step === 2) {
      // create rental + save checklist on the way to step 3
      await createRental();
      return;
    }
    if (step === 3) {
      // save checklist when moving from inspection to photos
      await saveChecklistStep();
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const createRental = async () => {
    setCreating(true);
    try {
      const payload = {
        car_id: car.id,
        customer_id: customer.id,
        pickup_date: period.pickup_date.toISOString(),
        return_date: period.return_date.toISOString(),
        billing_period: period.billing_period,
        daily_rate: dailyRate,
        weekly_rate: weeklyRate,
        deposit: Number(period.deposit || 0),
        total: rentalTotal,
        status: "ativo",
        contract_lang: contractLang,
      };
      const { data } = await api.post("/rentals", payload);
      setRentalId(data.id);
      // Persist preferred contract lang (best-effort, server already accepts it on create)
      try { await api.put(`/rentals/${data.id}/contract-lang`, { lang: contractLang }); } catch { /* ignore */ }
      toast.success(t("wizard.rentalCreated"));
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const saveChecklistStep = async () => {
    if (!rentalId) { setStep(4); return; }
    try {
      await api.post(`/rentals/${rentalId}/checklist`, {
        side: "pickup",
        items: checklist.items,
        fuel_level: Number(checklist.fuel_level),
        odometer: checklist.odometer === "" ? null : Number(checklist.odometer),
        notes: checklist.notes || null,
      });
      setStep(4);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const finishWizard = async (signatureB64) => {
    if (!rentalId) return;
    setSigning(true);
    try {
      // Make sure contract_lang is up to date
      try { await api.put(`/rentals/${rentalId}/contract-lang`, { lang: contractLang }); } catch { /* ignore */ }
      const { data } = await api.post(`/rentals/${rentalId}/signature`, { signature_b64: signatureB64 });
      if (data?.contract_stored) toast.success(t("wizard.completedWithContract"));
      else toast.success(t("wizard.completed"));
      navigate("/alugueis");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="rental-wizard">
      {/* Top bar */}
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MovixLogo variant="light" size="sm" showTagline={false} />
            <div className="hidden md:block h-6 w-px bg-zinc-700" />
            <div className="font-display font-bold tracking-tight uppercase text-sm">{t("wizard.title")}</div>
          </div>
          <button
            onClick={() => {
              if (window.confirm(t("wizard.cancelConfirm"))) navigate("/alugueis");
            }}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 px-3 py-1.5"
            data-testid="wizard-cancel"
          >
            <X className="w-3.5 h-3.5" /> {t("common.cancel")}
          </button>
        </div>
      </header>

      {/* Step indicator */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-4 overflow-x-auto">
          <div className="flex items-stretch gap-0 min-w-max">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const active = idx === step;
              const done = idx < step;
              return (
                <div key={s.key} className="flex items-center gap-3 pr-6" data-testid={`wizard-step-${idx}`}>
                  <div className={`relative w-9 h-9 flex items-center justify-center font-mono text-xs font-bold transition-all
                    ${done ? "bg-[#F5A623] text-white" : active ? "bg-black text-[#F5A623] ring-2 ring-[#F5A623]" : "bg-zinc-100 text-zinc-400"}`}>
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="flex flex-col">
                    <div className={`text-[10px] font-mono uppercase tracking-wider ${active ? "text-[#F5A623]" : "text-zinc-500"}`}>
                      {t(`wizard.steps.${s.key}.label`)}
                    </div>
                    <div className={`text-xs ${active ? "text-zinc-900 font-semibold" : done ? "text-zinc-600" : "text-zinc-400"}`}>
                      {t(`wizard.steps.${s.key}.short`)}
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-300 ml-3" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step content */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto p-6 pb-32">
          {step === 0 && <CustomerStep selected={customer} onSelect={setCustomer} />}
          {step === 1 && <CarStep selected={car} onSelect={setCar} />}
          {step === 2 && (
            <PeriodStep
              car={car}
              period={period}
              setPeriod={setPeriod}
              contractLang={contractLang}
              setContractLang={setContractLang}
              days={days}
              weeks={weeks}
              qty={qty}
              rate={rate}
              rentalTotal={rentalTotal}
              totalDue={totalDue}
              dateLocale={dateLocale}
            />
          )}
          {step === 3 && (
            <ChecklistStep
              checklist={checklist}
              setChecklist={setChecklist}
              car={car}
            />
          )}
          {step === 4 && <PhotosStep rentalId={rentalId} />}
          {step === 5 && (
            <SignatureStep
              car={car}
              customer={customer}
              rentalId={rentalId}
              rentalTotal={rentalTotal}
              deposit={Number(period.deposit || 0)}
              totalDue={totalDue}
              contractLang={contractLang}
              setContractLang={setContractLang}
              signing={signing}
              onFinish={finishWizard}
            />
          )}
        </div>
      </main>

      {/* Bottom action bar */}
      {step < 5 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3 pr-[220px]">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={step === 0 || creating}
              className="rounded-none h-12 px-6"
              data-testid="wizard-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> {t("common.back")}
            </Button>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hidden md:block">
              {t("wizard.stepN", { n: step + 1, total: STEPS.length })}
            </div>
            <Button
              type="button"
              onClick={goNext}
              disabled={!canNext || creating}
              className="rounded-none h-12 px-8 bg-[#F5A623] hover:bg-[#D97706] text-white text-base"
              data-testid="wizard-next"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t("common.next")} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// STEP 1 — CUSTOMER
// ============================================================================
function CustomerStep({ selected, onSelect }) {
  const { t } = useT();
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", social_security: "", driver_license: "",
    phone: "", email: "", address: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setList(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = list.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.phone || "").includes(s)
      || (c.driver_license || "").toLowerCase().includes(s);
  });

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/customers", form);
      onSelect(data);
      setCreating(false);
      toast.success(t("wizard.customer.created"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <StepHeader stepKey="customer" />

      {selected && (
        <SelectedCard
          title={selected.name}
          subtitle={`${selected.driver_license || "—"} · ${selected.phone || "—"}`}
          onClear={() => onSelect(null)}
          testid="wizard-selected-customer"
        />
      )}

      {!selected && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("wizard.customer.searchPlaceholder")}
                className="rounded-none pl-9 h-12 text-base"
                data-testid="wizard-customer-search"
              />
            </div>
            <Button
              type="button"
              onClick={() => setCreating(true)}
              className="rounded-none h-12 px-5 bg-black hover:bg-zinc-800 text-white"
              data-testid="wizard-customer-new"
            >
              <Plus className="w-4 h-4 mr-2" /> {t("wizard.customer.new")}
            </Button>
          </div>

          {creating && (
            <form onSubmit={submitNew} className="border border-[#F5A623] bg-orange-50/30 p-5 space-y-3" data-testid="wizard-new-customer-form">
              <div className="overline">{t("wizard.customer.newTitle")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label={t("customers.fullName")} required>
                  <Input className="rounded-none h-11" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </FormField>
                <FormField label={t("customers.phone")}>
                  <Input className="rounded-none h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </FormField>
                <FormField label={t("customers.ssn")}>
                  <Input className="rounded-none h-11" value={form.social_security} onChange={(e) => setForm({ ...form, social_security: e.target.value })} />
                </FormField>
                <FormField label={t("customers.license")}>
                  <Input className="rounded-none h-11" value={form.driver_license} onChange={(e) => setForm({ ...form, driver_license: e.target.value })} />
                </FormField>
                <FormField label={t("customers.email")}>
                  <Input className="rounded-none h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </FormField>
                <FormField label={t("customers.address")}>
                  <Input className="rounded-none h-11" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </FormField>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreating(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white">
                  <Check className="w-4 h-4 mr-2" /> {t("common.save")}
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <Loader />
          ) : filtered.length === 0 ? (
            <EmptyState text={t("wizard.customer.empty")} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.slice(0, 30).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelect(c)}
                  className="text-left border border-zinc-200 hover:border-[#F5A623] hover:bg-orange-50/30 bg-white p-4 transition-colors min-h-[88px]"
                  data-testid={`wizard-customer-${c.id}`}
                >
                  <div className="font-display font-bold tracking-tight">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5 flex flex-wrap gap-x-3">
                    {c.driver_license && <span>DL: {c.driver_license}</span>}
                    {c.phone && <span>{c.phone}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ============================================================================
// STEP 2 — CAR
// ============================================================================
function CarStep({ selected, onSelect }) {
  const { t, fmtCurrency } = useT();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/cars");
        setList(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const available = list.filter((c) => c.status === "disponivel" || (selected && c.id === selected.id));

  return (
    <div className="space-y-6">
      <StepHeader stepKey="car" />
      {selected && (
        <SelectedCard
          title={`${selected.model} · ${selected.plate}`}
          subtitle={`${selected.year} · ${selected.color} · ${fmtCurrency(selected.weekly_rate || (selected.daily_price||0)*7)}/week · ${fmtCurrency((selected.weekly_rate || (selected.daily_price||0)*7) / 7)}/day`}
          onClear={() => onSelect(null)}
          testid="wizard-selected-car"
        />
      )}
      {!selected && (
        loading ? <Loader /> :
        available.length === 0 ? <EmptyState text={t("wizard.car.empty")} /> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {available.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c)}
              className="text-left border border-zinc-200 hover:border-[#F5A623] hover:bg-orange-50/30 bg-white p-4 transition-colors min-h-[120px]"
              data-testid={`wizard-car-${c.plate}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{c.year} · {c.color}</div>
              <div className="font-display font-bold text-lg tracking-tight mt-0.5">{c.model}</div>
              <div className="font-mono text-xs text-zinc-600 mt-0.5">{c.plate}</div>
              <div className="mt-3 flex items-baseline gap-3">
                <div>
                  <div className="overline text-[9px]">{t("rentals.weekly")}</div>
                  <div className="font-mono font-bold text-[#F5A623]">{fmtCurrency(c.weekly_rate || (c.daily_price||0) * 7)}</div>
                </div>
                <div>
                  <div className="overline text-[9px]">{t("rentals.daily")}</div>
                  <div className="font-mono text-xs">{fmtCurrency((c.weekly_rate || (c.daily_price||0) * 7) / 7)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// STEP 3 — PERIOD + BILLING
// ============================================================================
function PeriodStep({ car, period, setPeriod, contractLang, setContractLang, days, weeks, qty, rate, rentalTotal, totalDue, dateLocale }) {
  const { t, fmtCurrency } = useT();
  return (
    <div className="space-y-6">
      <StepHeader stepKey="period" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label={t("rentals.pickup")}>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-none h-12 w-full justify-start font-normal text-base">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(period.pickup_date, "MM/dd/yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none">
              <Calendar mode="single" selected={period.pickup_date} onSelect={(d) => d && setPeriod({ ...period, pickup_date: d })} locale={dateLocale} />
            </PopoverContent>
          </Popover>
        </FormField>
        <FormField label={t("rentals.return")}>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-none h-12 w-full justify-start font-normal text-base">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(period.return_date, "MM/dd/yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none">
              <Calendar mode="single" selected={period.return_date} onSelect={(d) => d && setPeriod({ ...period, return_date: d })} locale={dateLocale} />
            </PopoverContent>
          </Popover>
        </FormField>
        <FormField label={t("rentals.billingType")}>
          <Select value={period.billing_period} onValueChange={(v) => setPeriod({ ...period, billing_period: v })}>
            <SelectTrigger className="rounded-none h-12 text-base" data-testid="wizard-billing-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="diaria">{t("rentals.daily")}</SelectItem>
              <SelectItem value="semanal">{t("rentals.weekly")}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={t("rentals.deposit")}>
          <Input
            type="number"
            className="rounded-none h-12 text-base font-mono"
            value={period.deposit}
            onChange={(e) => setPeriod({ ...period, deposit: e.target.value })}
            data-testid="wizard-deposit"
          />
        </FormField>
      </div>

      <div className="border-2 border-[#F5A623] bg-orange-50/40 p-5">
        <div className="overline mb-3">{t("wizard.period.summary")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Tile label={t("rentals.days")} value={`${days}`} />
          <Tile label={t("rentals.weeks")} value={`${weeks}`} />
          <Tile label={t("wizard.period.unitRate")} value={fmtCurrency(rate)} />
          <Tile label={t("wizard.period.qty")} value={`${qty}`} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#F5A623]/30">
          <Tile label={t("receipt.rental")} value={fmtCurrency(rentalTotal)} />
          <Tile label={t("rentalDetails.depositLabel")} value={fmtCurrency(period.deposit || 0)} />
          <Tile label={t("rentals.pickupTotal")} value={fmtCurrency(totalDue)} highlight />
        </div>
      </div>

      {/* Contract language pre-select */}
      <div className="border border-zinc-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><FileText className="w-3 h-3 text-[#F5A623]" /> {t("wizard.period.contractLang")}</div>
          <p className="text-xs text-zinc-500 mt-1">{t("wizard.period.contractLangHint")}</p>
        </div>
        <div className="inline-flex items-stretch border border-zinc-300 bg-white">
          {["pt", "en", "es"].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setContractLang(code)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-r last:border-r-0 border-zinc-300 ${
                contractLang === code ? "bg-[#F5A623] text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid={`wizard-lang-${code}`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// STEP 4 — CHECKLIST (pickup inspection)
// ============================================================================
function ChecklistStep({ checklist, setChecklist, car }) {
  const { t } = useT();
  const toggle = (k) => setChecklist({ ...checklist, items: { ...checklist.items, [k]: !checklist.items[k] } });
  const allCount = CHECKLIST_KEYS.length;
  const checkedCount = CHECKLIST_KEYS.filter((k) => checklist.items[k]).length;

  return (
    <div className="space-y-6">
      <StepHeader stepKey="checklist" extra={`${checkedCount}/${allCount}`} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 border border-zinc-200 bg-white p-4">
        {CHECKLIST_KEYS.map((k) => (
          <label
            key={k}
            className="flex items-center gap-3 cursor-pointer hover:bg-zinc-50 px-2 py-2 min-h-[44px]"
            data-testid={`wizard-checklist-${k}`}
          >
            <input
              type="checkbox"
              checked={!!checklist.items[k]}
              onChange={() => toggle(k)}
              className="w-5 h-5 accent-[#F5A623]"
            />
            <span className={`text-sm flex-1 ${checklist.items[k] ? "text-zinc-900" : "text-zinc-600"}`}>
              {t(`checklist.items.${k}`)}
            </span>
            {checklist.items[k] && <CheckCircle2 className="w-4 h-4 text-[#F5A623]" />}
          </label>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-zinc-200 bg-white p-4">
        <FormField label={t("checklist.fuelLevel")}>
          <div className="flex items-center gap-3">
            <Fuel className="w-4 h-4 text-[#F5A623]" />
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={checklist.fuel_level}
              onChange={(e) => setChecklist({ ...checklist, fuel_level: Number(e.target.value) })}
              className="flex-1 accent-[#F5A623]"
              data-testid="wizard-fuel"
            />
            <div className="font-mono text-sm font-bold w-14 text-right">{checklist.fuel_level}%</div>
          </div>
        </FormField>
        <FormField label={t("checklist.odometer")}>
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-[#F5A623]" />
            <Input
              type="number"
              className="rounded-none h-11"
              value={checklist.odometer || (car?.current_mileage ?? "")}
              onChange={(e) => setChecklist({ ...checklist, odometer: e.target.value })}
              placeholder="mi"
              data-testid="wizard-odometer"
            />
          </div>
        </FormField>
        <FormField label={t("common.notes")} fullWidth>
          <Input
            className="rounded-none h-11"
            value={checklist.notes}
            onChange={(e) => setChecklist({ ...checklist, notes: e.target.value })}
            placeholder={t("checklist.notesPlaceholder")}
            data-testid="wizard-checklist-notes"
          />
        </FormField>
      </div>
    </div>
  );
}


// ============================================================================
// STEP 5 — PHOTOS
// ============================================================================
function PhotosStep({ rentalId }) {
  const { t } = useT();
  return (
    <div className="space-y-6">
      <StepHeader stepKey="photos" />
      {rentalId ? (
        <PhotoGallery
          ownerType="rental_pickup"
          ownerId={rentalId}
          title={t("photos.pickupCondition")}
          description={t("photos.pickupConditionHint")}
          gridCols={4}
        />
      ) : (
        <EmptyState text={t("common.loading")} />
      )}
      <div className="text-xs text-zinc-500 border border-dashed border-zinc-300 p-3 bg-zinc-50/50">
        <Camera className="w-3 h-3 inline mr-1 text-[#F5A623]" />
        {t("wizard.photos.hint")}
      </div>
    </div>
  );
}


// ============================================================================
// STEP 6 — SIGNATURE + summary
// ============================================================================
function SignatureStep({ car, customer, rentalId, rentalTotal, deposit, totalDue, contractLang, setContractLang, signing, onFinish }) {
  const { t, fmtCurrency } = useT();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const tt = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: tt.clientX - rect.left, y: tt.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastRef.current = pos(e); setIsEmpty(false); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(lastRef.current.x, lastRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    lastRef.current = p;
  };
  const end = () => { drawingRef.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
  };

  const submit = () => {
    if (isEmpty || !rentalId) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onFinish(dataUrl);
  };

  return (
    <div className="space-y-6">
      <StepHeader stepKey="signature" />

      {/* Final review */}
      <div className="border-2 border-[#F5A623] bg-orange-50/40 p-5 space-y-3">
        <div className="overline">{t("wizard.signature.reviewTitle")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <KV label={t("receipt.customer")} value={customer?.name} />
          <KV label={t("receipt.car")} value={`${car?.model} · ${car?.plate}`} />
          <KV label={t("rentalDetails.depositLabel")} value={fmtCurrency(deposit)} />
          <KV label={t("receipt.rental")} value={fmtCurrency(rentalTotal)} />
        </div>
        <div className="pt-3 border-t border-[#F5A623]/30 flex items-center justify-between">
          <div className="overline">{t("rentals.pickupTotal")}</div>
          <div className="font-display font-black text-3xl tracking-tighter text-[#F5A623]">{fmtCurrency(totalDue)}</div>
        </div>
      </div>

      {/* Final lang toggle */}
      <div className="border border-zinc-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-zinc-700">
          <span className="font-semibold">{t("signature.langBannerTitle")}: </span>
          {t("signature.langBannerHint")}
        </div>
        <div className="inline-flex items-stretch border border-zinc-300 bg-white">
          {["pt", "en", "es"].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setContractLang(code)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-r last:border-r-0 border-zinc-300 ${
                contractLang === code ? "bg-[#F5A623] text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid={`wizard-final-lang-${code}`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {/* Big signature pad */}
      <div className="border-2 border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[#F5A623]" />
            <div className="overline">{t("signature.title")}</div>
          </div>
          <button
            type="button"
            onClick={clear}
            disabled={isEmpty}
            className="text-xs border border-zinc-300 hover:border-zinc-500 px-3 py-1.5 disabled:opacity-40"
            data-testid="wizard-sig-clear"
          >
            <Eraser className="w-3 h-3 inline mr-1" /> {t("signature.clear")}
          </button>
        </div>
        <div className="relative bg-[radial-gradient(circle,#E4E4E7_1px,transparent_1px)] [background-size:18px_18px]">
          <canvas
            ref={canvasRef}
            className="w-full h-64 touch-none cursor-crosshair block"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="wizard-sig-canvas"
          />
          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-zinc-400 font-mono uppercase tracking-[0.2em] text-xs">{t("signature.placeholder")}</div>
            </div>
          )}
        </div>
      </div>

      <Button
        type="button"
        onClick={submit}
        disabled={isEmpty || signing || !rentalId}
        className="w-full rounded-none h-14 bg-[#F5A623] hover:bg-[#D97706] text-white text-base font-bold tracking-tight mb-12"
        data-testid="wizard-finish"
      >
        {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
        {t("wizard.signature.finalize")}
      </Button>
    </div>
  );
}


// ============================================================================
// Helpers
// ============================================================================
function StepHeader({ stepKey, extra }) {
  const { t } = useT();
  return (
    <div>
      <div className="overline">{t("wizard.title")} · {t(`wizard.steps.${stepKey}.label`)}</div>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
          {t(`wizard.steps.${stepKey}.heading`)}
        </h1>
        {extra && <div className="font-mono text-sm text-[#F5A623]">{extra}</div>}
      </div>
      <p className="text-sm text-zinc-500 mt-1">{t(`wizard.steps.${stepKey}.description`)}</p>
    </div>
  );
}

const FormField = ({ label, required, children, fullWidth }) => (
  <div className={`space-y-1.5 ${fullWidth ? "md:col-span-2" : ""}`}>
    <label className="overline">{label}{required && <span className="text-[#F5A623]"> *</span>}</label>
    {children}
  </div>
);

function SelectedCard({ title, subtitle, onClear, testid }) {
  const { t } = useT();
  return (
    <div className="border-2 border-[#F5A623] bg-orange-50/30 p-4 flex items-center justify-between gap-3" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="bg-[#F5A623] text-white w-9 h-9 flex items-center justify-center">
          <Check className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display font-bold tracking-tight">{title}</div>
          <div className="text-xs text-zinc-600 font-mono mt-0.5">{subtitle}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="text-xs border border-zinc-300 hover:border-zinc-500 px-3 py-1.5"
      >
        <X className="w-3 h-3 inline mr-1" /> {t("wizard.change")}
      </button>
    </div>
  );
}

const Tile = ({ label, value, highlight }) => (
  <div className="border border-zinc-200 bg-white p-3">
    <div className="overline text-[9px]">{label}</div>
    <div className={`font-mono font-bold ${highlight ? "text-[#F5A623] text-xl" : "text-base"}`}>{value}</div>
  </div>
);

const KV = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
    <div className="font-semibold text-zinc-900 truncate">{value || "—"}</div>
  </div>
);

const Loader = () => (
  <div className="text-center py-12 text-zinc-500">
    <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#F5A623]" />
  </div>
);

const EmptyState = ({ text }) => (
  <div className="border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">{text}</div>
);
